import levenshtein from 'fast-levenshtein';
import type { CachedAsset } from '../types/cache';

/**
 * Calculate similarity between two strings using Levenshtein distance.
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @returns Similarity score as percentage (0-100)
 *
 * @example
 * calculateSimilarity('dragon', 'dragons') // Returns 86 (85.7%)
 * calculateSimilarity('cat', 'car') // Returns 67 (66.7%)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  // Normalize inputs: lowercase and trim (MUST match hash.ts normalization)
  const normalized1 = str1.toLowerCase().trim();
  const normalized2 = str2.toLowerCase().trim();

  // Handle edge case: both empty strings are identical
  if (normalized1 === '' && normalized2 === '') {
    return 100;
  }

  // Handle edge case: one string is empty
  if (normalized1 === '' || normalized2 === '') {
    return 0;
  }

  // Calculate Levenshtein distance
  const distance = levenshtein.get(normalized1, normalized2);

  // Calculate similarity: 1 - (distance / max_length)
  const maxLength = Math.max(normalized1.length, normalized2.length);
  const similarity = 1 - (distance / maxLength);

  // Return as percentage, rounded to nearest integer
  return Math.round(similarity * 100);
}

/**
 * Result from fuzzy matching search
 */
export interface FuzzyMatchResult {
  asset: CachedAsset | null;
  bestScore: number;
}

/**
 * Find the best fuzzy match for a prompt in the cache.
 *
 * Iterates through all cached assets of the specified type and calculates
 * similarity scores. Returns the asset with the highest score if it meets
 * or exceeds the threshold. Uses most recent timestamp as tie-breaker.
 *
 * @param db - IndexedDB database instance
 * @param prompt - User input prompt to match against
 * @param assetType - Type of asset to search ('character' | 'obstacle' | 'background')
 * @param threshold - Minimum similarity percentage (0-100) to consider a match
 * @returns Promise resolving to result object with best matching asset (or null) and best score
 *
 * @example
 * const result = await findBestFuzzyMatch(db, 'dragons', 'character', 70);
 * if (result.asset) {
 *   console.log(`Found match: ${result.asset.prompt} (${result.bestScore}%)`);
 * } else {
 *   console.log(`No match found. Best score: ${result.bestScore}%`);
 * }
 */
export function findBestFuzzyMatch(
  db: IDBDatabase,
  prompt: string,
  assetType: string,
  threshold: number
): Promise<FuzzyMatchResult> {
  return new Promise((resolve, reject) => {
    try {
      // Normalize input prompt once before iteration (performance optimization)
      const normalizedInput = prompt.toLowerCase().trim();

      const transaction = db.transaction(['assets'], 'readonly');
      const objectStore = transaction.objectStore('assets');
      const request = objectStore.openCursor();

      let bestMatch: CachedAsset | null = null;
      let bestScore = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const asset = cursor.value as CachedAsset;

          // Filter by assetType
          if (asset.assetType === assetType) {
            const score = calculateSimilarity(normalizedInput, asset.prompt);

            // Track highest score
            if (score > bestScore) {
              bestScore = score;
              bestMatch = asset;
            } else if (score === bestScore && bestMatch) {
              // Tie-breaker: most recently used (highest timestamp)
              if (asset.timestamp > bestMatch.timestamp) {
                bestMatch = asset;
              }
            }
            // Note: No early exit on 100% match to ensure tie-breaker logic works correctly
            // Performance impact is minimal since exact matches are handled before fuzzy matching
          }

          cursor.continue();
        } else {
          // No more records - return best match if above threshold
          if (bestMatch && bestScore >= threshold) {
            resolve({ asset: bestMatch, bestScore });
          } else {
            resolve({ asset: null, bestScore });
          }
        }
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}
